import { useLookbookWishlistContext } from '@app/components/lookbook/context/LookbookWishlistContext';
import { FavouritesFill, FavouritesOutline, Remove } from '@app/components/various/Icons';
import { useIsTouchScreen } from '@app/utils/hooks/useIsTouchScreen';
import { Product } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
interface Props {
  variant: Product.Standard;
}
export const VariantCardWishlistButton = ({ variant }: Props) => {
  const { t } = useTranslation(['selections']);
  const { wishlist, toggleProductInWishlist } = useLookbookWishlistContext();
  const isInWishlist = React.useMemo(() => wishlist.has(variant.product), [variant.product, wishlist]);
  const [shouldShowRemoveButton, setShouldShowRemoveButton] = React.useState(false);
  const isTouchScreen = useIsTouchScreen();

  const handleClick = () => {
    if (shouldShowRemoveButton) {
      setShouldShowRemoveButton(false);
    }

    toggleProductInWishlist(variant.product);
    setShouldShowRemoveButton(true);
  };

  const buttonIcon = React.useMemo(() => {
    if (!isInWishlist) {
      return <FavouritesOutline />;
    }

    if (shouldShowRemoveButton) {
      return <Remove />;
    }

    return <FavouritesFill />;
  }, [isInWishlist, shouldShowRemoveButton]);

  const buttonLabel = isInWishlist ? t('selections:remove_from_selection') : t('selections:add_to_selection');

  const buttonListeners = isInWishlist && {
    onBlur: () => setShouldShowRemoveButton(false),
    ...(!isTouchScreen && {
      onFocus: () => setShouldShowRemoveButton(true),
      onMouseEnter: () => setShouldShowRemoveButton(true),
      onMouseLeave: () => setShouldShowRemoveButton(false),
    }),
  };

  const testId = isInWishlist ? 'removeProductButton' : 'addProductButton';

  return (
    <div className="absolute right-0 top-0 rounded-full transition-colors group-hover/variant-card:text-neutral-40">
      <svg width="32" height="32" viewBox="0 0 32 32" fill="currentColor">
        <path d="M0 0H27.5C32.8752 0 32 0.754053 32 6.5V32C32 27.4286 28.5714 25.1429 22.8572 25.1429H19.8341C12.6671 25.1429 6.85715 18.9322 6.85715 11.2709V8C6.85714 3.42857 4.57143 0 0 0Z" />
      </svg>
      <button
        type="button"
        className="absolute right-1 top-1 rounded-full after:absolute after:-right-2 after:-top-2 after:block after:h-8 after:w-8 after:content-empty"
        aria-label={buttonLabel}
        data-testid={testId}
        onClick={handleClick}
        {...(isInWishlist && buttonListeners)}
      >
        <div className="text-white">{buttonIcon}</div>
      </button>
    </div>
  );
};
